import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useFamilies } from '@/data/families';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/admin/families/')({
  component: FamiliesList,
});

const PAGE_SIZE = 25;

function FamiliesList() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading, isError, error } = useFamilies({
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Roster
          </p>
          <h1 className="mt-2">Families</h1>
          {data && (
            <p className="mt-1 text-[var(--color-ink-muted)]">
              {data.total} {data.total === 1 ? 'family' : 'families'} active
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Link to="/admin/families/import">
            <Button variant="secondary">Import CSV</Button>
          </Link>
          <Link to="/admin/families/new">
            <Button>New family</Button>
          </Link>
        </div>
      </div>

      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
        }}
        className="max-w-md"
      >
        <Label htmlFor="family-search">Search</Label>
        <Input
          id="family-search"
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Guardian name or email"
          autoComplete="off"
        />
      </form>

      {isLoading && (
        <p className="text-[var(--color-ink-subtle)]" aria-live="polite">
          Loading…
        </p>
      )}

      {isError && (
        <p role="alert" className="text-[var(--color-danger)]">
          {error instanceof Error ? error.message : 'Failed to load families.'}
        </p>
      )}

      {data?.rows.length === 0 && !isLoading && (
        <Empty
          title={search ? 'No families match your search' : 'No families yet'}
          description={
            search
              ? 'Try a different name or email, or clear the search to see everyone.'
              : 'Start by importing your roster CSV or adding the first family by hand.'
          }
          action={
            !search && (
              <div className="flex gap-2">
                <Link to="/admin/families/import">
                  <Button variant="secondary">Import CSV</Button>
                </Link>
                <Link to="/admin/families/new">
                  <Button>Add a family</Button>
                </Link>
              </div>
            )
          }
        />
      )}

      {data && data.rows.length > 0 && (
        <>
          <div role="region" aria-label="Families table" className="surface overflow-hidden">
            <table className="w-full text-left">
              <caption className="sr-only">All active families</caption>
              <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Guardian
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Phone
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    City
                  </th>
                  <th scope="col" className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.rows.map((f) => {
                  const address = f.address as { city?: string };
                  return (
                    <tr
                      key={f.id}
                      className="border-t border-[var(--color-border)] hover:bg-[var(--color-surface-sunken)]"
                    >
                      <td className="px-4 py-3 font-medium">{f.primary_guardian_name}</td>
                      <td className="px-4 py-3 text-[var(--color-ink-muted)]">
                        {f.primary_guardian_email}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-ink-muted)] tabular-nums">
                        {f.primary_guardian_phone}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-ink-muted)]">
                        {address.city ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to="/admin/families/$id"
                          params={{ id: f.id }}
                          className="text-[var(--color-accent)] underline-offset-2 hover:underline"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav
              aria-label="Pagination"
              className="flex items-center justify-between gap-4 text-[var(--text-sm)]"
            >
              <span className="text-[var(--color-ink-muted)]">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
